import { zodResolver } from "@hookform/resolvers/zod";
import { createId } from "@paralleldrive/cuid2";
import { Plus } from "lucide-react";
import { type UseFormReturn, useFieldArray, useForm } from "react-hook-form";

import {
  type ModifierFormSchema,
  modifierFormSchema,
} from "../../../lib/validators/modifiers";
import { Accordion } from "../../ui/accordion";
import { Button } from "../../ui/button";
import { DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { defaultNewFormValues } from "./common";
import { ModifierListItem } from "./modifier-list-item";

interface ModifierFormProps {
  editMode: boolean;
  values?: ModifierFormSchema;
  onSubmit: (data: ModifierFormSchema) => void;
}

export function ModifierForm({
  editMode,
  values,
  onSubmit,
}: ModifierFormProps) {
  const form = useForm<ModifierFormSchema>({
    resolver: zodResolver(modifierFormSchema),
    defaultValues: values ?? defaultNewFormValues,
  });

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.stopPropagation();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <ModifierFormContent editMode={editMode} form={form} />
        {Object.keys(form.formState.errors).length > 0 && (
          <div className="border-destructive/20 bg-destructive/10 rounded-lg border p-3">
            <p className="label text-destructive">
              Please fix the following errors:
            </p>
            <ul className="caption text-destructive mt-2 list-inside list-disc">
              {form.formState.errors.name && (
                <li>Modifier group name is required</li>
              )}
              {form.formState.errors.minimum && (
                <li>{form.formState.errors.minimum.message}</li>
              )}
              {form.formState.errors.maximum && (
                <li>{form.formState.errors.maximum.message}</li>
              )}
              {form.formState.errors.modifiersList && (
                <li>Please fix modifier errors below</li>
              )}
            </ul>
          </div>
        )}
        <Button className="w-full" size="lg" type="submit">
          Next
        </Button>
      </form>
    </Form>
  );
}

export function ModifierFormContent({
  editMode,
  form,
}: {
  editMode: boolean;
  form: UseFormReturn<ModifierFormSchema>;
}) {
  const modifiersListField = useFieldArray({
    control: form.control,
    name: "modifiersList",
    rules: {
      minLength: 1,
    },
  });

  return (
    <div className="space-y-5">
      {!editMode && (
        <DialogHeader className="items-center space-y-0">
          <DialogTitle>Add modifiers</DialogTitle>
          <DialogDescription>
            How do you want to add modifiers?
          </DialogDescription>
        </DialogHeader>
      )}
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Modifier Group Name</FormLabel>
            <FormControl>
              <Input
                {...field}
                onChange={(e) => {
                  return field.onChange(e.target.value);
                }}
                value={field.value}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-2 gap-5">
        <FormField
          control={form.control}
          name="minimum"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Minimum Selection</FormLabel>
              <FormControl>
                <Input
                  min={0}
                  onChange={(e) => {
                    const value = e.currentTarget.valueAsNumber;
                    field.onChange(Number.isNaN(value) ? 0 : value);
                  }}
                  onFocus={(e) => e.target.select()}
                  type="number"
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="maximum"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Maximum Selection</FormLabel>
              <FormControl>
                <Input
                  min={1}
                  onChange={(e) => {
                    const value = e.currentTarget.valueAsNumber;
                    field.onChange(Number.isNaN(value) ? 1 : value);
                  }}
                  onFocus={(e) => e.target.select()}
                  type="number"
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Accordion
        onValueChange={(ids) => {
          modifiersListField.fields.forEach((item, index) => {
            modifiersListField.update(index, {
              ...item,
              accordionOpen: ids.includes(item.itemId),
            });
          });
        }}
        type="multiple"
        value={modifiersListField.fields
          .filter((item) => item.accordionOpen)
          .map((item) => item.itemId)}
      >
        <fieldset className="space-y-2">
          <Label asChild>
            <legend>Modifiers</legend>
          </Label>
          {modifiersListField.fields.map((modifier, index) => (
            <ModifierListItem
              fieldArray={modifiersListField}
              form={form}
              index={index}
              key={modifier.itemId}
              modifier={modifier}
            />
          ))}
          <button
            className="text-success flex items-center gap-1"
            onClick={() => {
              const modifierNumber = modifiersListField.fields.length + 1;
              modifiersListField.append({
                itemId: createId(),
                name: `New modifier ${modifierNumber}`,
                modifierType: "modifier",
                accordionOpen: true,
                sortOrder: modifiersListField.fields.length,
                data: {
                  additionalCost: "0.00",
                  isOutOfStock: false,
                  canBeMultiplied: false,
                },
              });
            }}
            type="button"
          >
            <Plus className="size-4" />
            Add modifier
          </button>
        </fieldset>
      </Accordion>
    </div>
  );
}
